import type { Section } from "./types";

export const industries = [
	"Manufacturing",
	"Retail",
	"Healthcare",
	"Logistics",
	"Financial Services",
	"Technology",
	"Education",
	"Construction",
	"Energy",
	"Other",
];

export const employeeSizes = [
	"1-10",
	"11-50",
	"51-200",
	"201-500",
	"501-1,000",
	"1,001-10,000",
	"10,000+",
];

export const sections: Section[] = [
	{
		id: "data",
		shortLabel: "Data",
		title: "Data Infrastructure & Quality",
		intro:
			"Assess whether your data foundation is ready for reliable AI outcomes.",
		questions: [
			{
				id: "d1",
				label:
					"How centralized and accessible is your production and quality data (ERP, MES, IoT)?",
				options: [
					{ value: 100, label: "Fully centralized and standardized" },
					{ value: 70, label: "Partially centralized, some silos" },
					{ value: 40, label: "Mostly manual or spreadsheet-based" },
					{ value: 10, label: "Scattered and inconsistent" },
				],
			},
			{
				id: "d2",
				label: "Do you maintain historical data for analytics or forecasting?",
				options: [
					{ value: 100, label: "3+ years of clean historical data" },
					{ value: 70, label: "1-3 years, partly structured" },
					{ value: 40, label: "Less than 1 year of partial data" },
					{ value: 10, label: "Data rarely archived or reused" },
				],
			},
			{
				id: "d3",
				label: "How consistent and validated is your data across systems?",
				options: [
					{ value: 100, label: "Automated validation & deduplication in place" },
					{ value: 70, label: "Manual checks performed periodically" },
					{ value: 40, label: "Ad hoc validation when needed" },
					{ value: 10, label: "No standard validation process" },
				],
			},
		],
	},
	{
		id: "process",
		shortLabel: "Process",
		title: "Process Maturity & Automation",
		intro:
			"Identify where process standardization and automation can unlock AI ROI.",
		questions: [
			{
				id: "p1",
				label: "Are your standard operating procedures (SOPs) digital and version-controlled?",
				options: [
					{ value: 100, label: "Fully digital and automated" },
					{ value: 70, label: "Partially digitized" },
					{ value: 40, label: "Manual documents or spreadsheets" },
					{ value: 10, label: "Not standardized" },
				],
			},
			{
				id: "p2",
				label: "How automated are your production and quality workflows?",
				options: [
					{ value: 100, label: "Highly automated with sensor feedback loops" },
					{ value: 70, label: "Moderately automated with operator input" },
					{ value: 40, label: "Low automation (manual triggers)" },
					{ value: 10, label: "Fully manual" },
				],
			},
			{
				id: "p3",
				label:
					"How frequently do you measure and analyze process efficiency metrics (OEE, yield, downtime)?",
				options: [
					{ value: 100, label: "Real-time monitoring and analytics dashboards" },
					{ value: 70, label: "Weekly or monthly reporting" },
					{ value: 40, label: "Occasional manual analysis" },
					{ value: 10, label: "Rarely measured or tracked" },
				],
			},
		],
	},
	{
		id: "people",
		shortLabel: "People",
		title: "People, Skills & Culture",
		intro:
			"Evaluate team capability and leadership readiness to execute AI initiatives.",
		questions: [
			{
				id: "pe1",
				label: "Does your team include or have access to AI/data analytics expertise?",
				options: [
					{ value: 100, label: "Dedicated in-house team" },
					{ value: 70, label: "Shared resource or external consultant" },
					{ value: 40, label: "Limited analytics skills internally" },
					{ value: 10, label: "No analytics expertise" },
				],
			},
			{
				id: "pe2",
				label: "How open is leadership to experimenting with AI and emerging technologies?",
				options: [
					{ value: 100, label: "Strong leadership support & budget allocation" },
					{ value: 70, label: "Moderate interest, pilot budgets" },
					{ value: 40, label: "Skeptical but observing peers" },
					{ value: 10, label: "Not a current focus" },
				],
			},
			{
				id: "pe3",
				label:
					"How frequently do cross-functional teams collaborate (IT, operations, maintenance, quality)?",
				options: [
					{ value: 100, label: "Daily collaboration on shared goals" },
					{ value: 70, label: "Regular meetings weekly or biweekly" },
					{ value: 40, label: "Occasional communication" },
					{ value: 10, label: "Work in isolated silos" },
				],
			},
		],
	},
	{
		id: "technology",
		shortLabel: "Technology",
		title: "Technology Stack & Security",
		intro:
			"Understand if your technical environment can support scalable AI adoption.",
		questions: [
			{
				id: "t1",
				label: "What best describes your current technology environment?",
				options: [
					{ value: 100, label: "Cloud-enabled, API-integrated systems" },
					{ value: 70, label: "Mostly on-prem with limited integration" },
					{ value: 40, label: "Legacy systems with occasional upgrades" },
					{ value: 10, label: "Fragmented legacy stack" },
				],
			},
			{
				id: "t2",
				label: "Have you implemented IoT or sensor-based monitoring on your key equipment?",
				options: [
					{ value: 100, label: "Extensive across majority of equipment" },
					{ value: 70, label: "Partial coverage on critical lines" },
					{ value: 40, label: "Early-stage pilot or proof of concept" },
					{ value: 10, label: "No IoT implementation" },
				],
			},
			{
				id: "t3",
				label: "How mature is your cybersecurity and data governance practice?",
				options: [
					{ value: 100, label: "Comprehensive, audited annually" },
					{ value: 70, label: "Documented policies, limited audits" },
					{ value: 40, label: "Basic controls, informal policies" },
					{ value: 10, label: "Minimal awareness or controls" },
				],
			},
		],
	},
	{
		id: "strategy",
		shortLabel: "Strategy",
		title: "AI & Innovation Strategy",
		intro:
			"Capture strategic alignment and current AI experimentation maturity.",
		questions: [
			{
				id: "s1",
				label: "Do you have a defined digital transformation or AI roadmap?",
				options: [
					{ value: 100, label: "Yes, clearly defined and funded" },
					{ value: 70, label: "In development with leadership backing" },
					{ value: 40, label: "Discussed but no roadmap" },
					{ value: 10, label: "Not yet considered" },
				],
			},
			{
				id: "s2",
				label:
					"Have you piloted or implemented any AI-driven solutions (e.g., predictive maintenance, demand forecasting)?",
				options: [
					{ value: 100, label: "Multiple pilots live or scaling" },
					{ value: 70, label: "One successful pilot in progress" },
					{ value: 40, label: "Early exploration or vendor demos" },
					{ value: 10, label: "No AI initiatives yet" },
				],
			},
		],
	},
];
